import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { Modal } from '../ui/Modal';
import { useToast } from '../ui/Toast';
import { Badge } from '../ui/Badge';

interface Token {
  id: string;
  label: string;
  tokenHint: string;
  createdAt: string;
  lastUsedAt?: string | null;
  revokedAt?: string | null;
}

interface IssuedToken extends Token { token: string }

export function AppPasswordsPage() {
  const [tokens, setTokens] = useState<Token[]>([]);
  const [label, setLabel] = useState('');
  const [issued, setIssued] = useState<IssuedToken | null>(null);
  const toast = useToast();

  const load = () => api.get<Token[]>('/api/app-passwords').then(setTokens);
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!label.trim()) {
      toast.error('Bitte ein Label vergeben.');
      return;
    }
    const res = await api.post<IssuedToken>('/api/app-passwords', { label });
    setIssued(res);
    setLabel('');
    load();
  };

  const revoke = async (id: string) => {
    await api.delete(`/api/app-passwords/${id}`);
    toast.success('Token widerrufen');
    load();
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="card">
        <h1 className="text-h3 mb-2">App-Passwoerter</h1>
        <p className="text-body text-ink-secondary">
          App-Passwoerter werden in CalDAV-Clients (iPhone, eM Client, Android-Apps) als Passwort eingegeben.
          Das Klartext-Token wird nur einmalig angezeigt.
        </p>
      </div>

      <div className="card">
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="label">Label</label>
            <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="z.B. iPhone Privat" />
          </div>
          <button className="btn btn-primary" onClick={handleCreate}>Erzeugen</button>
        </div>
      </div>

      <div className="card !p-0 overflow-hidden">
        <table className="w-full text-body">
          <thead className="bg-surface-elevated text-meta uppercase tracking-[0.16em] text-ink-muted">
            <tr>
              <th className="text-left px-4 py-3">Label</th>
              <th className="text-left px-4 py-3">Hint</th>
              <th className="text-left px-4 py-3">Erstellt</th>
              <th className="text-left px-4 py-3">Zuletzt benutzt</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {tokens.map((t) => (
              <tr key={t.id} className="table-row">
                <td className="px-4 py-3">{t.label}</td>
                <td className="px-4 py-3 font-mono text-small text-ink-muted">{t.tokenHint}…</td>
                <td className="px-4 py-3 text-ink-secondary">{new Date(t.createdAt).toLocaleString('de-DE')}</td>
                <td className="px-4 py-3 text-ink-secondary">{t.lastUsedAt ? new Date(t.lastUsedAt).toLocaleString('de-DE') : '—'}</td>
                <td className="px-4 py-3"><Badge tone={t.revokedAt ? 'danger' : 'success'}>{t.revokedAt ? 'widerrufen' : 'aktiv'}</Badge></td>
                <td className="px-4 py-3 text-right">
                  {!t.revokedAt && <button className="btn btn-danger" onClick={() => revoke(t.id)}>Widerrufen</button>}
                </td>
              </tr>
            ))}
            {tokens.length === 0 && (
              <tr><td colSpan={6} className="text-center text-ink-muted py-8 text-small">Noch keine Tokens erzeugt.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal open={!!issued} onClose={() => setIssued(null)} title="App-Passwort erzeugt" footer={
        <button className="btn btn-primary" onClick={() => setIssued(null)}>Verstanden</button>
      }>
        <p className="text-body text-ink-secondary mb-3">
          Kopiere das Token jetzt. Es wird <strong>nicht erneut</strong> angezeigt.
        </p>
        <div className="font-mono text-body bg-surface-elevated border border-border rounded-md p-3 break-all">
          {issued?.token}
        </div>
      </Modal>
    </div>
  );
}
